import { defineStore } from 'pinia';
import { ref } from 'vue';
import backendApi from '../axios/backendApi';
import { backend_url } from '../config/config';

export interface TrainingSession {
  jour: string;
  horaire: string;
  local: string;
  adresse: string;
  code_postal: string;
  ville: string;
  telephone: string;
}

export interface PublicClub {
  id: number;
  number: number;
  name: string;
  logo?: string | null;
  email?: string | null;
  lat?: number | null;
  lng?: number | null;
  trainings: TrainingSession[];
}

export const useClubsStore = defineStore('clubs', () => {
  const clubMapping = ref<Record<string, string>>({});
  // Nom -> code : contrairement à clubMapping (code -> nom), ne perd pas les clubs dont
  // le code est partagé avec un autre comité (SDC, CDC, BDC...).
  const clubCodeByName = ref<Record<string, string>>({});
  const clubLogoByNumber = ref<Record<number, string>>({});
  const loaded = ref(false);
  const publicClubs = ref<PublicClub[]>([]);
  const publicClubsLoaded = ref(false);

  const load = async () => {
    if (loaded.value) return;
    try {
      const { data } = await backendApi.get('/clubs/config');
      clubMapping.value = data.clubMapping ?? {};
      clubCodeByName.value = data.clubCodeByName ?? {};
      clubLogoByNumber.value = data.clubLogoByNumber ?? {};
      loaded.value = true;
    } catch {
      // silencieux — les helpers retournent le code brut en fallback
    }
  };

  const loadPublicClubs = async (comiteCode: string) => {
    if (publicClubsLoaded.value) return;
    try {
      const { data } = await backendApi.get('/clubs/public', { params: { comite_code: comiteCode } });
      publicClubs.value = data;
      publicClubsLoaded.value = true;
    } catch {
      // silencieux — la page affiche une liste vide en fallback
    }
  };

  const getClubName = (code: string): string =>
    clubMapping.value[code] ?? code;

  const getClubLogoUrl = (number: number): string => {
    const logo = clubLogoByNumber.value[number];
    if (!logo) return new URL('../assets/clubs/nologo.png', import.meta.url).href;
    if (logo.startsWith('/uploads/')) return `${backend_url}${logo}`;
    return new URL(`../assets/clubs/${logo}`, import.meta.url).href;
  };

  return { clubMapping, clubCodeByName, clubLogoByNumber, loaded, load, getClubName, getClubLogoUrl, publicClubs, publicClubsLoaded, loadPublicClubs };
});
